import { kitchenApi } from './kitchenApi';
import type { UserDto } from '../api-types';
import type { NotificationSettings } from '../api-types/models/UserDto';
import type { SportradarUser } from '../interfaces/sportradarUser';

export interface PaginatedUsersParams {
    pageSize?: number;
    nextPage?: string;
}

export interface PaginatedUsersResponse {
    items: SportradarUser[];
    nextPage?: string;
}

export const userApi = kitchenApi.injectEndpoints({
    endpoints: (builder) => ({
        getUserSettings: builder.query<UserDto, void>({
            query: () => ({
                apiName: 'brunch-v2',
                path: '/user',
                method: 'GET'
            }),
            providesTags: ['UserSettings']
        }),

        updateUserSettings: builder.mutation<UserDto, UserDto>({
            query: (body) => ({
                apiName: 'brunch-v2',
                path: '/user',
                method: 'PUT',
                body
            }),
            invalidatesTags: ['UserSettings']
        }),

        getUsers: builder.query<SportradarUser[], void>({
            query: () => ({
                apiName: 'brunch-v2',
                path: '/user/all',
                method: 'GET'
            }),
            providesTags: ['Users']
        }),

        getUsersPaginated: builder.query<
            PaginatedUsersResponse,
            PaginatedUsersParams
        >({
            query: (params) => ({
                apiName: 'brunch-v2',
                path: '/user/list',
                method: 'GET',
                queryParams: {
                    ...(params.pageSize !== undefined && {
                        pageSize: String(params.pageSize)
                    }),
                    ...(params.nextPage !== undefined && {
                        nextPage: params.nextPage
                    })
                }
            }),
            providesTags: ['Users']
        }),

        updateUser: builder.mutation<SportradarUser, SportradarUser>({
            query: (body) => ({
                apiName: 'brunch-v2',
                path: '/user/payment',
                method: 'PUT',
                body
            }),
            invalidatesTags: ['Users']
        }),

        updateNotificationSettings: builder.mutation<
            void,
            NotificationSettings
        >({
            query: (settings) => ({
                apiName: 'brunch-v2',
                path: '/user/notifications',
                method: 'PUT',
                body: Object.keys(settings).map((key) => ({
                    notificationType: key,
                    value: settings[key as keyof NotificationSettings]
                }))
            }),
            invalidatesTags: ['UserSettings']
        }),

        updateFoodFilter: builder.mutation<
            void,
            { filter: string; value: boolean }
        >({
            query: (body) => ({
                apiName: 'brunch-v2',
                path: '/user/filter',
                method: 'PUT',
                body
            }),
            invalidatesTags: ['UserSettings']
        }),

        updateAllergies: builder.mutation<
            void,
            { allergen: string; value: boolean }
        >({
            query: (body) => ({
                apiName: 'brunch-v2',
                path: '/user/allergies',
                method: 'PUT',
                body
            }),
            invalidatesTags: ['UserSettings']
        }),

        addGuest: builder.mutation<UserDto, string>({
            query: (guest) => ({
                apiName: 'brunch-v2',
                path: `/user/guest/add/${guest}`,
                method: 'PUT'
            }),
            invalidatesTags: ['UserSettings']
        }),

        removeGuest: builder.mutation<UserDto, string>({
            query: (guest) => ({
                apiName: 'brunch-v2',
                path: `/user/guest/remove/${guest}`,
                method: 'PUT'
            }),
            invalidatesTags: ['UserSettings']
        })
    }),
    overrideExisting: true
});

export const {
    useGetUserSettingsQuery,
    useUpdateUserSettingsMutation,
    useGetUsersQuery,
    useGetUsersPaginatedQuery,
    useUpdateUserMutation,
    useUpdateNotificationSettingsMutation,
    useUpdateFoodFilterMutation,
    useUpdateAllergiesMutation,
    useAddGuestMutation,
    useRemoveGuestMutation
} = userApi;
